import { Profile } from '../../models/profile';
import { AppState } from '../index';
import { createSelector } from '@ngrx/store';
import { RoleEnum } from '../../enum/role.enum';
import { ServerMessage } from '../../models/server-message';
import { SettingEnum } from '../../enum/setting.enum';
import { ShipmentView } from '../../models/shipment-view';
import { defaultShipmentViews } from '../../constants/default-shipment-views';
import { OptionTagEnum } from '../../enum/option-tag.enum';
import { TermsEnum } from '../../enum/terms.enum';

export interface ProfileState {
    busy: boolean;
    profile: Profile;
    errors: ServerMessage[];
}

export const selectProfileState = (state: AppState) => state.profile;

export const selectConsumerTheme = createSelector(selectProfileState,
    (state: ProfileState) => state.profile?.consumerTheme?.split(';')[0]);

export const selectThemeFlag = (flag: string) => createSelector(selectProfileState,
    (state: ProfileState) => state.profile?.consumerTheme ? state.profile.consumerTheme.split(';').indexOf(flag) !== -1 : false,
);

export const selectUserAvatar = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.userAvatar);

export const selectProfileBusy = createSelector(selectProfileState,
    (state: ProfileState) => state.busy);

export const selectProfileReady = createSelector(selectProfileState,
    (state: ProfileState) => !state.busy);

export const selectProfileIdentityName = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.identityName);

export const selectProfileLogo = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.logoUrl);

export const selectProfileSquareLogo = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.squareLogoUrl);

export const selectCourierProfile = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.courierConfiguration);

export const selectPackagingTypes = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.courierConfiguration.packagingTypes);

export const selectCourierOptions = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.courierConfiguration.options);

export const selectSelectableCourierOptions = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.courierConfiguration.options.filter(o => o.tags.includes(OptionTagEnum.SELECTABLE)));

export const selectCourierForms = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.courierConfiguration.forms);

export const selectCourierDetails = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.courierConfiguration.couriers);

export const selectReferenceGroups = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.interfaceModification?.refs?.grouping);

export const selectReferenceDefinitions = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.interfaceModification?.refs);

export const selectThirdPartyAccountLimited = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.interfaceModification.third_party_accounts.length > 0);

export const selectThirdPartyAccountChoices = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.interfaceModification.third_party_accounts);

export const selectMessagingUrl = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.messageUrl);

export const selectNewShipmentTemplate = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.newShipmentTemplate);

export const selectIconSet = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.icons);

export const selectProfileTimestamp = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.timestamp);

export const selectRole = (role: string) => createSelector(selectProfileState,
    (state: ProfileState) => state.profile.roles.indexOf(role) !== -1);

export const selectSetting = (setting: string) => createSelector(selectProfileState,
    (state: ProfileState) => state.profile.settings ? state.profile.settings.find(s => s.id === setting)?.value : null);

export const selectSettingEditable = (setting: string) => createSelector(selectProfileState,
    (state: ProfileState) => state.profile.settings ? !state.profile.settings.find(s => s.id === setting)?.readOnly : false);

export const selectEditableSettings = (path: string) => createSelector(selectProfileState,
    (state: ProfileState) => state.profile.settings ? state.profile.settings
        .filter(s => s.definition.path.indexOf(path) !== -1)
        .filter(s => !s.readOnly)
        .map(s => s.definition) : []
);

export const selectThirdPartyAccountAllowed = selectRole(RoleEnum.THIRD_PARTY_SHIPMENT);

export const selectSubmitShipmentAllowed = selectRole(RoleEnum.MANIFEST_SHIPMENTS);

export const selectShipmentViews = createSelector(selectProfileState,
    (state: ProfileState) => {
        const available = { ...defaultShipmentViews };
        if (state.profile.settings) {
            state.profile.settings
                .filter(s => s.id === SettingEnum.MANAGED_SHIPMENT_VIEWS)
                .map(s => s.value as ShipmentView[])
                .flat()
                .filter(view => !!view)
                .forEach(view => available[view.id] = view);
            state.profile.settings
                .filter(s => s.id === SettingEnum.USER_SHIPMENT_VIEWS)
                .map(s => s.value as ShipmentView[])
                .flat()
                .filter(view => !!view)
                .forEach(view => available[view.id] = view);
        }
        const views: ShipmentView[] = Object.values(available);
        return views.filter(v => !v.hide);
    });

export const selectInsuranceCategories = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.insuranceCategories);

export const selectProfileCurrency = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.currency);

export const selectOrganizationName = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.organizationName);

export const selectUserEmail = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.email);

/** The authenticated user's username (profile id). */
export const selectProfileUsername = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.id);

export const selectProfileNotices = (noticeType: string[]) => createSelector(selectProfileState,
    (state: ProfileState) => state.profile.notices.filter(n => noticeType.includes(n.type)));

export const selectProfilePriceDisplay = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.pricingDisplay);

export const selectProfileTermsAgreed = (terms: TermsEnum) => createSelector(selectProfileState,
    (state: ProfileState) => state.profile.outstandingTerms.find(t => t.type === terms) === null);

export const selectProfileCurrencies = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.currencyConversion);

export const selectProfileBanner = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.bannerUrl);

export const selectProfileGeneralTermsUrl = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.generalTermsUrl);

export const selectProfilePrivacyPolicyUrl = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.privacyPolicyUrl);

export const selectProfileImpersonator = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.impersonator);

export const selectProfileImpersonationMenu = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.impersonationMenu);

export const selectProfileLogins = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.logins);

export const selectProfileFullName = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.fullName);

export const selectProfileBoxPackingTemplates = createSelector(selectProfileState,
    (state: ProfileState) => state.profile.packageTemplates.filter(p => !!p.boxPacking));
